// Authentik API helpers — uses the user's OAuth2 access token
// Requires `goauthentik.io/api` scope on the OAuth2 application

import { getValidAccessToken } from './auth';

const API_BASE = 'https://authentik.vinayakrobotics.com/api/v3';

async function api(path, options = {}) {
  const token = await getValidAccessToken();
  if (!token) throw new Error('Not authenticated');

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`API ${res.status}: ${body}`);
  }
  if (res.status === 204) return null;
  return res.json();
}

// ── User Profile ──

export async function getMe() {
  return api('/core/users/me/');
}

export async function updateUser(userId, data) {
  return api(`/core/users/${userId}/`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function setPassword(userId, password) {
  return api(`/core/users/${userId}/set_password/`, {
    method: 'POST',
    body: JSON.stringify({ password }),
  });
}

// ── Avatar ──
// Authentik stores avatar in user attributes.
// Upload requires a flow with file prompt — for now we use URL-based avatars.

export async function setAvatarUrl(userId, url) {
  return updateUser(userId, {
    attributes: { avatar: url },
  });
}

// ── Sessions ──

// Authenticated sessions (Authentik browser sessions — has IP info)
export async function listSessions() {
  return api('/core/authenticated_sessions/');
}

export async function revokeSession(uuid) {
  return api(`/core/authenticated_sessions/${uuid}/`, {
    method: 'DELETE',
  });
}

// OAuth2 refresh tokens — shows per-app active sessions
export async function listRefreshTokens() {
  return api('/oauth2/refresh_tokens/');
}

export async function revokeRefreshToken(id) {
  return api(`/oauth2/refresh_tokens/${id}/`, {
    method: 'DELETE',
  });
}

// ── TOTP 2FA ──

export async function listTotpDevices() {
  return api('/authenticators/totp/');
}

export async function deleteTotpDevice(id) {
  return api(`/authenticators/totp/${id}/`, {
    method: 'DELETE',
  });
}

// ── Discourse Sync ──
// Discourse uses OIDC with Authentik — profile changes sync on next Discourse login.
// For immediate sync, a server-side bridge would call Discourse's sync_sso endpoint.
// This is noted here for documentation; actual sync requires a backend service.
